import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog } from '@material-ui/core';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { getErrorData } from 'common/constants/get-error-data';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import CollapsibleCard from 'core/components/collapsible-card';
import {
  typeToName,
  buildPricingRows,
  buildSectionChanges,
  parseDiff,
  buildReportResultsShownEvent,
  REPORT_RESULTS_SHOWN_EVENT
} from './helpers';

import documentChecked from './assets/document-checked.svg';
import offerUpdateIcon from './assets/offer-update.svg';
import reportNoChanges from 'core/assets/svg/document-approval-green.svg';
import checkGreen from 'core/assets/svg/ic-check-green.svg';
import alertOrange from 'core/assets/svg/ic-rejection-no-background.svg';
import rightArrow from 'core/assets/svg/ic-arrow.svg';
import Rejection from 'core/assets/svg/ic-rejection.svg';
import Solution from 'core/assets/svg/ic-solution.svg';
import StepsToResolve from 'core/assets/svg/ic-steps-to-resolve.svg';

import useStyles from './modal-offer-diff.styles';

const BufferedCollapsibleCard = ({ children, ...props }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <CollapsibleCard
      {...props}
      onChange={setOpen}
      collapsedSize={64}
      className={`${classes.collapsibleCardBuffer} ${open ? classes.collapsibleCardBufferOpen : ''}`}
    >
      {children}
    </CollapsibleCard>
  );
};

const OfferDifferenceModal = ({ open }) => {
  const classes = useStyles();
  const {
    offer: { offer, offerDifference, clearOfferDifference, state }
  } = useStore();
  const { isAgency } = useSession();
  const fromRC1 = offer?.quote?.global?.fromRC1;

  const { added, removed } = parseDiff(offerDifference);
  const eligibilityChanges = offerDifference?.eligibilityChanges ?? [];

  const pricingRows = buildPricingRows({ offer, removed, eligibilityChanges });
  const sections = buildSectionChanges({ added, removed, offer, eligibilityChanges });

  const newlyIneligibleRejections = eligibilityChanges
    .filter((c) => c.wasEligible && !c.isEligible)
    .flatMap((c) => (c.rejectionCodesAdded ?? []).map((code) => ({ policyType: c.policyType, code })));

  const rejectionCount = newlyIneligibleRejections.length;
  const hasAnyContent = rejectionCount > 0 || sections.length > 0;

  // Observer re-renders on unrelated store updates; key the one event per open on the frozen offerDifference reference.
  const trackedDifferenceRef = useRef(null);
  useEffect(() => {
    if (!open || !offerDifference || trackedDifferenceRef.current === offerDifference) {
      return;
    }
    trackedDifferenceRef.current = offerDifference;
    track(
      REPORT_RESULTS_SHOWN_EVENT,
      buildReportResultsShownEvent({ offer, state, isAgency, sections, pricingRows, rejectionCount, removed })
    );
  }, [open, offerDifference, offer, state, isAgency, sections, pricingRows, rejectionCount, removed]);

  return (
    <Dialog open={open} maxWidth="xl" classes={{ paper: classes.dialogPaper }}>
      <img
        src={fromRC1 ? offerUpdateIcon : documentChecked}
        className={classes.icon}
        alt={fromRC1 ? 'Paper with two arrows forming a circle' : 'Paper with a check mark'}
      />
      <Label className={classes.title} type="h1">
        Report results
      </Label>
      <div>
        <Label type="body1">
          {fromRC1
            ? 'This offer’s coverages and prices have been updated'
            : 'We’ve updated this offer with new report information. Price or eligibility may have changed.'}
        </Label>
        <div>
          <section className={classes.pricingSection}>
            <Label type="subtitle" className={classes.sectionHeading}>
              Pricing changes
            </Label>
            <div className={classes.pricingCard}>
              {pricingRows.map((row, idx) => (
                <div key={row.label} className={`${classes.pricingRow} ${idx > 0 ? classes.pricingRowBorder : ''}`}>
                  <Label type="body1" className={classes.pricingLabel}>
                    {row.label}
                  </Label>
                  <div className={classes.pricingValues}>
                    {row.changed && (
                      <>
                        <span className={classes.pricingOld}>{row.oldValue}</span>
                        <img src={rightArrow} alt="to" className={classes.changeArrow} />
                      </>
                    )}
                    {row.newlyIneligible ? (
                      <span className={classes.ineligibleChip}>
                        <img src={alertOrange} alt="" className={classes.ineligibleIcon} />
                        Ineligible
                      </span>
                    ) : (
                      <span className={classes.pricingNew}>{row.newValue}</span>
                    )}
                    {!row.changed && !row.newlyIneligible && (
                      <>
                        <img src={rightArrow} alt="to" className={classes.changeArrow} />
                        <span className={classes.noChangesChip}>
                          <img src={checkGreen} alt="" className={classes.ineligibleIcon} />
                          No changes
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {hasAnyContent ? (
            <>
              <section className={classes.whatChangedSection}>
                <Label type="subtitle">What changed</Label>
                <Label type="body2" className={classes.whatChangedSubtitle}>
                  These updates explain the changes to price or eligibility.
                </Label>
              </section>

              <div className={classes.whatChangedCards}>
                {newlyIneligibleRejections.length > 0 && (
                  <BufferedCollapsibleCard
                    content={
                      <div className={classes.collapsibleCardHeaderLeft}>
                        <Label type="infoSubtitle">Eligibility issues</Label>
                      </div>
                    }
                    contentRight={
                      <div className={classes.collapsibleCardHeaderRight}>
                        <Label type="infoLabel">{newlyIneligibleRejections.length} items</Label>
                      </div>
                    }
                  >
                    <div className={classes.rejectionList}>
                      {newlyIneligibleRejections.map((error) => {
                        const rejectionData = getErrorData({
                          error,
                          isAgency,
                          state: state,
                          errorType: 'REJECTION'
                        });

                        return (
                          <div
                            className={classes.rejection}
                            key={`rejection-${rejectionData.code}-{rejectionData.policyType}`}
                          >
                            <div className={classes.inside}>
                              <div className={classes.topRow}>
                                <div className={classes.row}>
                                  <img src={Rejection} alt="rejection" />
                                  <div className={classes.titleColumn}>
                                    <div className={classes.code}>
                                      {`rejection: ${rejectionData.code}`.toUpperCase()}
                                    </div>
                                    <div className={classes.errorTitle}>
                                      {typeToName[rejectionData.policyType]
                                        ? `${typeToName[rejectionData.policyType]} ineligible: `
                                        : 'Ineligible: '}
                                      {rejectionData.title}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {rejectionData.why && (
                                <div className={classes.subBox}>
                                  <div className={classes.iconRow}>
                                    <img src={Solution} alt="why?" className={classes.errorIcon} /> WHY?
                                  </div>
                                  <div className={classes.boxContent}>{rejectionData.why}</div>
                                </div>
                              )}
                              {rejectionData.nextSteps && (
                                <div className={classes.subBox}>
                                  <div className={classes.iconRow}>
                                    <img src={StepsToResolve} alt="Steps To Resolve" className={classes.errorIcon} />{' '}
                                    STEPS TO RESOLVE
                                  </div>
                                  <div className={classes.boxContent}>{rejectionData.nextSteps}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </BufferedCollapsibleCard>
                )}

                {sections.map((section) => {
                  const totalRows = Object.values(section.sections).reduce((sum, { rows }) => sum + rows.length, 0);
                  return (
                    <BufferedCollapsibleCard
                      content={
                        <div className={classes.collapsibleCardHeaderLeft}>
                          <Label type="infoSubtitle">{section.title}</Label>
                        </div>
                      }
                      key={section.key}
                      contentRight={
                        <div className={classes.collapsibleCardHeaderRight}>
                          <Label type="infoLabel">{totalRows} items</Label>
                        </div>
                      }
                    >
                      {Object.entries(section.sections).map(([subSectionToAddKey, { primary, coApp, rows }]) => (
                        <React.Fragment key={subSectionToAddKey}>
                          <div className={classes.subSectionTitleRow}>
                            <Label type="sectionSubtitle" className={classes.subSectionTitle}>
                              {subSectionToAddKey}
                            </Label>
                            {primary && <Label type="infoCardTitle">Applicant</Label>}
                            {coApp && <Label type="infoCardTitle">Co-applicant</Label>}
                          </div>
                          <div className={classes.changeGroup}>
                            <div className={classes.subSection}>
                              {rows.map((row) => {
                                return row.newValue || row.oldValue ? (
                                  <div key={row.path} className={classes.changeRow}>
                                    <Label type="body2" className={classes.changeLabel}>
                                      {row.label}
                                    </Label>
                                    <div className={classes.changeValues}>
                                      {row.oldValue !== null && row.oldValue !== undefined && (
                                        <span className={classes.oldValue}>{row.oldValue}</span>
                                      )}
                                      {row.oldValue !== null &&
                                        row.oldValue !== undefined &&
                                        row.newValue !== null &&
                                        row.newValue !== undefined && <span className={classes.newValue}>→</span>}

                                      {(row.oldValue === null || row.oldValue === undefined) &&
                                        !row.hideAddedLabel &&
                                        row.newValue !== null &&
                                        row.newValue !== undefined && <span className={classes.oldValue}>Added: </span>}

                                      {row.newValue !== null && row.newValue !== undefined && (
                                        <span className={classes.newValue}>{row.newValue}</span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div key={row.path} className={classes.changeRow}>
                                    <div className={classes.noPriceDataRow}>
                                      <Label type="body2">{row.label}</Label>
                                      {row.subtitle && <span className={classes.claimSubtitle}>{row.subtitle}</span>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </BufferedCollapsibleCard>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={classes.noChangesReport}>
              <span className={classes.noChangesCard}>
                <img src={reportNoChanges} alt="" className={classes.noChangesIcon} />
                No new information was found in the reports.
              </span>
            </div>
          )}
        </div>
        <div className={classes.footer}>
          <Button
            variant="contained"
            color="primary"
            className={classes.footerButton}
            onClick={() => clearOfferDifference()}
          >
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

OfferDifferenceModal.propTypes = {
  open: PropTypes.bool.isRequired
};

export default observer(OfferDifferenceModal);
